import { useState } from 'react';

export type SortDir = 'asc' | 'desc' | null;
export type SortState<K extends string> = { key: K; dir: 'asc' | 'desc' } | null;

/**
 * Hook that manages client-side sort state for a table.
 * Click toggles: asc → desc → none.
 */
export function useTableSort<K extends string>(defaultKey?: K, defaultDir?: 'asc' | 'desc') {
  const [sort, setSort] = useState<SortState<K>>(
    defaultKey && defaultDir ? { key: defaultKey, dir: defaultDir } : null,
  );

  const toggle = (key: K) => {
    setSort((prev) => {
      if (prev?.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });
  };

  return { sort, toggle } as const;
}

/**
 * Generic comparator that sorts items by a given key accessor.
 */
export function sortItems<T>(
  items: T[],
  sort: SortState<string>,
  accessors: Record<string, (item: T) => string | number | null>,
): T[] {
  if (!sort) return items;
  const accessor = accessors[sort.key];
  if (!accessor) return items;

  return [...items].sort((a, b) => {
    const va = accessor(a);
    const vb = accessor(b);
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    const cmp =
      typeof va === 'string'
        ? va.localeCompare(vb as string, 'sv')
        : (va as number) - (vb as number);
    return sort.dir === 'desc' ? -cmp : cmp;
  });
}
